/**
 * DocumentsAdmin Component
 *
 * Purpose: Admin interface for managing airline-specific document libraries (SOPs, rubrics, manuals)
 *
 * Features:
 * - Upload documents (PDF, DOCX, TXT)
 * - View document list with filtering (type, language, status)
 * - Re-ingest documents (when SOPs updated)
 * - Deactivate/activate documents
 * - Delete documents (with confirmation)
 * - View document details (chunks, embeddings, ingestion status)
 *
 * Sprint: 3.5 (pgvector Integration)
 * Author: Edward Berger + AI Assistant
 * Date: 2025-10-30
 * Last Updated: 2025-11-05 - Hybrid container + Supabase architecture with root npm install fix
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useAirline } from '../../contexts/AirlineContext';
import { DocLibrary, FleetModel, DocType, TrainingLevel } from '../../lib/types';
import {
  Upload,
  FileText,
  RefreshCw,
  Trash2,
  Eye,
  Search,
  CheckCircle,
  XCircle,
  Loader,
  Archive,
  RotateCcw,
  Info,
  Settings,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

// =====================================================================
// Types & Interfaces
// =====================================================================

// Use DocLibrary from types.ts for full schema support
type Document = DocLibrary;

interface FilterState {
  doc_type: string;
  language: string;
  status: string;
  airline: string;
}

// =====================================================================
// Main Component
// =====================================================================

export const DocumentsAdmin: React.FC = () => {
  const { user } = useAuth();
  const { selectedAirline, userAirlines } = useAirline();

  // State
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<FilterState>({
    doc_type: 'all',
    language: 'all',
    status: 'active',
    airline: selectedAirline?.airline_id || 'all', // Default to current airline
  });

  // Reference data
  const [fleetModels, setFleetModels] = useState<FleetModel[]>([]);
  const [docTypes, setDocTypes] = useState<DocType[]>([]);
  const [airlines, setAirlines] = useState<Array<{ id: string; name: string; code: string }>>([]);

  // Modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [showAdvancedParams, setShowAdvancedParams] = useState(false);

  // Edit form state (local changes before save)
  const [editFormData, setEditFormData] = useState<Partial<Document>>({});
  const [isSaving, setIsSaving] = useState(false);
  
  // Upload form state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadForm, setUploadForm] = useState({
    airline_id: selectedAirline?.airline_id || '', // Note: org_id column references airlines(id) table
    title: '',
    doc_type: 'CoreSystem' as Document['doc_type'],
    language: 'en',
    version: 'v1.0',
    is_active: true,
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Re-ingestion concurrency control (max 1 doc at a time to avoid 502 overload)
  const [reingestingDocIds, setReingestingDocIds] = useState<Set<string>>(new Set());
  const ingestionQueueRef = useRef<Array<{ doc: Document; resolve: () => void; reject: (e: Error) => void }>>([]);
  const ingestionActiveRef = useRef(false);

  // Versioning & replacement state
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [replaceContext, setReplaceContext] = useState<{
    existingDoc: Document;
    nextVersion: string;
    withIngest: boolean;
  } | null>(null);

  // Helpers: filename and version parsing
  const getFilenameSansExt = (name: string): string => {
    const idx = name.lastIndexOf('.');
    return idx > 0 ? name.slice(0, idx) : name;
  };

  const parseLeadingVersionNumber = (version: string | null | undefined): number => {
    if (!version) return 0;
    const m = /^v?(\d+)/i.exec(version.trim());
    const numString = m?.[1] ?? '0';
    const parsed = parseInt(numString, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const nextAutoVersion = (currentVersion: string | null | undefined): string => {
    const n = parseLeadingVersionNumber(currentVersion);
    return `v${n + 1}`;
  };

  // ===================================================================
  // Update airline_id in upload form when selectedAirline changes
  // ===================================================================

  useEffect(() => {
    if (selectedAirline?.airline_id) {
      setUploadForm(prev => ({ ...prev, airline_id: selectedAirline.airline_id }));
    }
  }, [selectedAirline]);

  // ===================================================================
  // Load reference data (fleet models, doc types, airlines)
  // ===================================================================

  useEffect(() => {
    const loadReferenceData = async () => {
      // Load fleet models
      const { data: fleets, error: fleetsError } = await supabase
        .from('fleet_models')
        .select('*')
        .order('manufacturer, code');

      if (fleetsError) {
        console.error('[DocumentsAdmin] Failed to load fleet models:', fleetsError);
      } else {
        setFleetModels(fleets || []);
      }

      // Load doc types
      const { data: types, error: typesError } = await supabase
        .from('doc_types')
        .select('*')
        .order('code');

      if (typesError) {
        console.error('[DocumentsAdmin] Failed to load doc types:', typesError);
      } else {
        setDocTypes(types || []);
      }

      // Load airlines
      const { data: airlinesList, error: airlinesError } = await supabase
        .from('airlines')
        .select('id, name, code')
        .order('name');

      if (airlinesError) {
        console.error('[DocumentsAdmin] Failed to load airlines:', airlinesError);
      } else {
        setAirlines(airlinesList || []);
      }
    };

    loadReferenceData();
  }, []);

  // ===================================================================
  // Load documents
  // ===================================================================

  useEffect(() => {
    loadDocuments();
  }, [filters]);

  const loadDocuments = async () => {
    setLoading(true);
    
    try {
      let query = supabase
        .from('doc_library')
        .select('*')
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.doc_type !== 'all') {
        query = query.eq('doc_type', filters.doc_type);
      }

      if (filters.language !== 'all') {
        query = query.eq('language', filters.language);
      }

      if (filters.status === 'active') {
        query = query.eq('is_active', true);
      } else if (filters.status === 'inactive') {
        query = query.eq('is_active', false);
      }

      // Apply airline filter (org_id column references airlines(id) table)
      if (filters.airline !== 'all') {
        query = query.eq('org_id', filters.airline);
      }

      const { data, error } = await query;

      if (error) {
        console.error('[DocumentsAdmin] Load error:', error);
        alert('Failed to load documents. Please try again.');
        return;
      }

      setDocuments(data || []);
    } catch (error) {
      console.error('[DocumentsAdmin] Unexpected error:', error);
    } finally {
      setLoading(false);
    }
  };

  // ===================================================================
  // Upload document (without ingestion)
  // ===================================================================

  const handleUploadOnly = async () => {
    if (!uploadFile || !user) {
      alert('Please select a file and ensure you are logged in.');
      return;
    }

    if (!uploadForm.airline_id) {
      alert('Please select an airline to assign this document to.');
      return;
    }

    if (!uploadForm.title.trim()) {
      alert('Please enter a document title.');
      return;
    }

    // Check for existing document with same title to trigger replace flow
    console.log('[DocumentsAdmin] Checking for existing doc with title:', uploadForm.title, 'airline:', uploadForm.airline_id);
    const { data: existingDocs, error: existingErr } = await supabase
      .from('doc_library')
      .select('*')
      .eq('org_id', uploadForm.airline_id)
      .eq('title', uploadForm.title.trim())
      .order('created_at', { ascending: false })
      .limit(1);

    if (existingErr) {
      console.error('[DocumentsAdmin] Pre-check failed:', existingErr);
    }

    console.log('[DocumentsAdmin] Existing docs found:', existingDocs?.length || 0, existingDocs);

    if (existingDocs && existingDocs.length > 0) {
      const existing = existingDocs[0] as Document;
      console.log('[DocumentsAdmin] Triggering replace modal for existing doc:', existing.id, existing.version);
      setShowUploadModal(false); // Close upload modal first
      setReplaceContext({ existingDoc: existing, nextVersion: nextAutoVersion(existing.version), withIngest: false });
      setShowReplaceModal(true);
      return;
    }

    console.log('[DocumentsAdmin] No existing doc found, proceeding with normal upload');

    setUploading(true);
    setUploadProgress(10);

    try {
      // Step 1: Create document record
      const { data: docData, error: docError } = await supabase
        .from('doc_library')
        .insert({
          org_id: uploadForm.airline_id,
          title: uploadForm.title,
          doc_type: uploadForm.doc_type,
          language: uploadForm.language,
          version: uploadForm.version,
          is_active: uploadForm.is_active,
          storage_path: '', // Will update after upload
          file_size_bytes: uploadFile.size,
          created_by: user.id,
        })
        .select()
        .single();

      if (docError) {
        console.error('[DocumentsAdmin] Create document error:', docError);
        alert('Failed to create document record.');
        setUploading(false);
        return;
      }

      setUploadProgress(40);

      // Step 2: Upload file to Supabase Storage
      const storagePath = `/org/${uploadForm.airline_id}/docs/${docData.id}/${uploadFile.name}`;

      const { error: storageError } = await supabase.storage
        .from('documents')
        .upload(storagePath, uploadFile, {
          cacheControl: '3600',
          upsert: false,
        });

      if (storageError) {
        console.error('[DocumentsAdmin] Storage upload error:', storageError);
        alert('Failed to upload file to storage.');

        // Rollback: delete document record
        await supabase.from('doc_library').delete().eq('id', docData.id);
        setUploading(false);
        return;
      }

      setUploadProgress(80);

      // Step 3: Update document with storage path
      const { error: updateError } = await supabase
        .from('doc_library')
        .update({ storage_path: storagePath })
        .eq('id', docData.id);

      if (updateError) {
        console.error('[DocumentsAdmin] Update storage path error:', updateError);
      }

      setUploadProgress(100);

      // Success!
      alert(`Document "${uploadForm.title}" uploaded successfully! You can ingest it later using the Re-Ingest button.`);
      setShowUploadModal(false);
      resetUploadForm();
      loadDocuments();
    } catch (error) {
      console.error('[DocumentsAdmin] Upload error:', error);
      alert('An unexpected error occurred during upload.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // ===================================================================
  // Upload document with ingestion
  // ===================================================================

  const handleUploadAndIngest = async () => {
    if (!uploadFile || !user) {
      alert('Please select a file and ensure you are logged in.');
      return;
    }

    if (!uploadForm.airline_id) {
      alert('Please select an airline to assign this document to.');
      return;
    }

    if (!uploadForm.title.trim()) {
      alert('Please enter a document title.');
      return;
    }

    // Check for existing document with same title to trigger replace flow
    console.log('[DocumentsAdmin] (Ingest) Checking for existing doc with title:', uploadForm.title, 'airline:', uploadForm.airline_id);
    const { data: existingDocs, error: existingErr } = await supabase
      .from('doc_library')
      .select('*')
      .eq('org_id', uploadForm.airline_id)
      .eq('title', uploadForm.title.trim())
      .order('created_at', { ascending: false })
      .limit(1);

    if (existingErr) {
      console.error('[DocumentsAdmin] (Ingest) Pre-check failed:', existingErr);
    }

    console.log('[DocumentsAdmin] (Ingest) Existing docs found:', existingDocs?.length || 0, existingDocs);

    if (existingDocs && existingDocs.length > 0) {
      const existing = existingDocs[0] as Document;
      console.log('[DocumentsAdmin] (Ingest) Triggering replace modal for existing doc:', existing.id, existing.version);
      setShowUploadModal(false); // Close upload modal first
      setReplaceContext({ existingDoc: existing, nextVersion: nextAutoVersion(existing.version), withIngest: true });
      setShowReplaceModal(true);
      return;
    }

    console.log('[DocumentsAdmin] (Ingest) No existing doc found, proceeding with normal upload');

    setUploading(true);
    setUploadProgress(10);

    try {
      // Step 1: Create document record
      const { data: docData, error: docError} = await supabase
        .from('doc_library')
        .insert({
          org_id: uploadForm.airline_id, // Note: org_id column references airlines(id) table
          title: uploadForm.title,
          doc_type: uploadForm.doc_type,
          language: uploadForm.language,
          version: uploadForm.version,
          is_active: uploadForm.is_active,
          storage_path: '', // Will update after upload
          file_size_bytes: uploadFile.size,
          created_by: user.id,
        })
        .select()
        .single();

      if (docError) {
        console.error('[DocumentsAdmin] Create document error:', docError);
        alert('Failed to create document record.');
        setUploading(false);
        return;
      }

      setUploadProgress(30);

      // Step 2: Upload file to Supabase Storage
      const storagePath = `/org/${uploadForm.airline_id}/docs/${docData.id}/${uploadFile.name}`;

      const { error: storageError } = await supabase.storage
        .from('documents')
        .upload(storagePath, uploadFile, {
          cacheControl: '3600',
          upsert: false,
        });

      if (storageError) {
        console.error('[DocumentsAdmin] Storage upload error:', storageError);
        alert('Failed to upload file to storage.');

        // Rollback: delete document record
        await supabase.from('doc_library').delete().eq('id', docData.id);
        setUploading(false);
        return;
      }

      setUploadProgress(60);

      // Step 3: Update document with storage path
      const { error: updateError } = await supabase
        .from('doc_library')
        .update({ storage_path: storagePath })
        .eq('id', docData.id);

      if (updateError) {
        console.error('[DocumentsAdmin] Update storage path error:', updateError);
      }

      setUploadProgress(80);

      // Step 4: Initialize resumable ingestion and process all batches
      await orchestrateIngestion({
        docId: docData.id,
        orgId: uploadForm.airline_id,
        storagePath,
        reset: true,
      });

      setUploadProgress(100);

      // Success!
      alert(`Document "${uploadForm.title}" uploaded and ingested successfully!`);
      setShowUploadModal(false);
      resetUploadForm();
      loadDocuments();
    } catch (error) {
      console.error('[DocumentsAdmin] Upload error:', error);
      alert('An unexpected error occurred during upload.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadForm({
      airline_id: selectedAirline?.airline_id || '',
      title: '',
      doc_type: 'CoreSystem',
      language: 'en',
      version: 'v1.0',
      is_active: true,
    });
  };

  // ---------------------------------------------------------------
  // Replace & Archive flow
  // ---------------------------------------------------------------
  const performReplaceAndArchive = async () => {
    if (!replaceContext || !uploadFile || !user || !uploadForm.airline_id) return;

    const { existingDoc, nextVersion, withIngest } = replaceContext;

    setUploading(true);
    setUploadProgress(10);

    try {
      // 1) Create new document row for next version
      const { data: newDoc, error: createErr } = await supabase
        .from('doc_library')
        .insert({
          org_id: uploadForm.airline_id,
          title: uploadForm.title,
          doc_type: uploadForm.doc_type,
          language: uploadForm.language,
          version: nextVersion,
          is_active: true,
          storage_path: '',
          file_size_bytes: uploadFile.size,
          created_by: user.id,
        })
        .select()
        .single();

      if (createErr || !newDoc) {
        console.error('[DocumentsAdmin] Create new version failed:', createErr);
        alert('Failed to create new version record.');
        setUploading(false);
        return;
      }

      setUploadProgress(35);

      // 2) Upload new file under new doc id
      const newStoragePath = `/org/${uploadForm.airline_id}/docs/${newDoc.id}/${uploadFile.name}`;
      const { error: uploadErr } = await supabase.storage
        .from('documents')
        .upload(newStoragePath, uploadFile, { cacheControl: '3600', upsert: false });
      if (uploadErr) {
        console.error('[DocumentsAdmin] New file upload failed:', uploadErr);
        alert('Failed to upload new version file to storage.');
        await supabase.from('doc_library').delete().eq('id', newDoc.id);
        setUploading(false);
        return;
      }

      setUploadProgress(60);

      // 3) Update new doc with storage path
      await supabase.from('doc_library').update({ storage_path: newStoragePath }).eq('id', newDoc.id);

      // 4) Move old file to archive and update old row
      const oldPath = existingDoc.storage_path;
      const filename = oldPath.split('/').pop();
      const archivePath = `/org/${existingDoc.org_id}/docs/archive/${existingDoc.id}/${existingDoc.version || 'v1'}/${filename}`;
      const { error: moveErr } = await supabase.storage.from('documents').move(oldPath, archivePath);
      if (moveErr) {
        console.error('[DocumentsAdmin] Move to archive failed:', moveErr);
        // Non-fatal; keep going to avoid blocking
      }
      await supabase
        .from('doc_library')
        .update({ storage_path: archivePath, is_active: false })
        .eq('id', existingDoc.id);

      setUploadProgress(80);

      // 5) Optional ingestion
      if (withIngest) {
        await orchestrateIngestion({ docId: newDoc.id, orgId: uploadForm.airline_id, storagePath: newStoragePath, reset: true });
      }

      setUploadProgress(100);
      alert(`${nextVersion} created. Previous version archived.`);
      setShowReplaceModal(false);
      setReplaceContext(null);
      setShowUploadModal(false);
      resetUploadForm();
      loadDocuments();
    } catch (e) {
      console.error('[DocumentsAdmin] Replace & Archive error:', e);
      alert('An unexpected error occurred while replacing the document.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const performUploadAsNew = async () => {
    if (!uploadFile || !user || !uploadForm.airline_id) return;
    const withIngest = !!replaceContext?.withIngest;
    setShowReplaceModal(false);
    setReplaceContext(null);

    setUploading(true);
    setUploadProgress(10);
    try {
      // Create row
      const { data: docData, error: docError } = await supabase
        .from('doc_library')
        .insert({
          org_id: uploadForm.airline_id,
          title: uploadForm.title,
          doc_type: uploadForm.doc_type,
          language: uploadForm.language,
          version: uploadForm.version,
          is_active: uploadForm.is_active,
          storage_path: '',
          file_size_bytes: uploadFile.size,
          created_by: user.id,
        })
        .select()
        .single();

      if (docError || !docData) {
        alert('Failed to create document record.');
        setUploading(false);
        return;
      }

      setUploadProgress(40);

      // Upload file
      const storagePath = `/org/${uploadForm.airline_id}/docs/${docData.id}/${uploadFile.name}`;
      const { error: storageError } = await supabase.storage
        .from('documents')
        .upload(storagePath, uploadFile, { cacheControl: '3600', upsert: false });
      if (storageError) {
        await supabase.from('doc_library').delete().eq('id', docData.id);
        alert('Failed to upload file to storage.');
        setUploading(false);
        return;
      }

      setUploadProgress(70);
      await supabase.from('doc_library').update({ storage_path: storagePath }).eq('id', docData.id);

      if (withIngest) {
        setUploadProgress(85);
        await orchestrateIngestion({ docId: docData.id, orgId: uploadForm.airline_id, storagePath, reset: true });
      }

      setUploadProgress(100);
      alert(withIngest ? 'New document created and ingested.' : 'New document created.');
      setShowUploadModal(false);
      resetUploadForm();
      loadDocuments();
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // ===================================================================
  // Re-ingest document (with concurrency queue — max 1 at a time)
  // ===================================================================

  const processIngestionQueue = useCallback(async () => {
    if (ingestionActiveRef.current) return; // already processing
    ingestionActiveRef.current = true;

    while (ingestionQueueRef.current.length > 0) {
      const item = ingestionQueueRef.current.shift()!;
      const { doc, resolve, reject } = item;

      setReingestingDocIds(prev => new Set(prev).add(doc.id));
      try {
        await orchestrateIngestion({
          docId: doc.id,
          orgId: doc.org_id,
          storagePath: doc.storage_path,
          reset: true,
        });
        console.log(`[DocumentsAdmin] Re-ingested "${doc.title}" successfully`);
        resolve();
      } catch (error) {
        console.error(`[DocumentsAdmin] Re-ingest failed for "${doc.title}":`, error);
        reject(error instanceof Error ? error : new Error(String(error)));
      } finally {
        setReingestingDocIds(prev => {
          const next = new Set(prev);
          next.delete(doc.id);
          return next;
        });
      }
    }

    ingestionActiveRef.current = false;
    loadDocuments();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReingest = async (doc: Document) => {
    if (reingestingDocIds.has(doc.id)) {
      alert(`"${doc.title}" is already being re-ingested.`);
      return;
    }

    if (!confirm(`Re-ingest "${doc.title}"? This will delete existing chunks and re-process the document.`)) {
      return;
    }

    const queuePos = ingestionQueueRef.current.length + (ingestionActiveRef.current ? 1 : 0);
    if (queuePos > 0) {
      alert(`"${doc.title}" queued for re-ingestion (position ${queuePos + 1}). Documents are processed one at a time to avoid server overload.`);
    }

    try {
      await new Promise<void>((resolve, reject) => {
        ingestionQueueRef.current.push({ doc, resolve, reject });
        processIngestionQueue();
      });
      alert(`Document "${doc.title}" re-ingested successfully!`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      alert(`Re-ingestion failed for "${doc.title}": ${errorMessage}`);
    }
  };

  // ===================================================================
  // Ingestion Orchestrator (Resumable) - Hybrid Container + Supabase Architecture
  // ===================================================================
  const orchestrateIngestion = async ({ docId, orgId, storagePath, reset }: { docId: string; orgId: string; storagePath: string; reset?: boolean; }) => {
    // Start
    const startResp = await supabase.functions.invoke('ingest-doc', {
      body: {
        action: 'start',
        doc_id: docId,
        org_id: orgId,
        storage_path: storagePath,
        reset: !!reset,
      },
    });
    if (startResp.error) {
      throw new Error(await extractFunctionError(startResp));
    }
    const run = (startResp.data as any)?.run;
    if (!run) {
      throw new Error('Failed to initialize ingestion run');
    }

    let pageBatchSize: number = run.page_batch_size || 8;
    let totalPages: number = run.total_pages || 1; // Changed from const to let for PDF hybrid architecture
    let nextPageStart: number = run.next_page_start || 1;

    // Detect if this is a PDF file
    const isPDF = storagePath.toLowerCase().endsWith('.pdf');

    // Scale up batch size for large PDFs to avoid re-downloading/re-parsing the full file hundreds of times
    if (isPDF && totalPages > 100) {
      pageBatchSize = Math.min(20, Math.max(pageBatchSize, Math.ceil(totalPages / 80)));
      console.log(`[DocumentsAdmin] Large PDF (${totalPages} pages), using batch size ${pageBatchSize}`);
    }

    // Process in loops
    while (nextPageStart <= totalPages) {
      let preExtractedText: string | null = null;
      let preExtractedTotalPages: number | null = null;

      // HYBRID ARCHITECTURE: For PDFs, extract text via container API (60s timeout, better CPU)
      if (isPDF) {
        try {
          // For first iteration, we don't know total pages yet - extract a reasonable batch
          // API will tell us the actual total, then we can adjust
          let pageEnd = Math.min(nextPageStart + pageBatchSize - 1, totalPages > 1 ? totalPages : 1000);

          console.log(`[DocumentsAdmin] Calling API for PDF extraction (pages ${nextPageStart}-${pageEnd})`);

          const {
            data: { session },
          } = await supabase.auth.getSession();
          const accessToken = session?.access_token;
          if (!accessToken) {
            throw new Error('Authentication required. Please sign in again.');
          }

          const pdfResp = await fetch('/api/process-pdf', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              storage_path: storagePath,
              page_start: nextPageStart,
              page_end: pageEnd,
            }),
          });

          if (!pdfResp.ok) {
            let errorMsg = pdfResp.statusText;
            try {
              const errorData = await pdfResp.json();
              errorMsg = errorData.error || errorMsg;
            } catch {
              // Response body is not JSON (e.g., "Bad Gateway" from proxy)
              errorMsg = await pdfResp.text().catch(() => errorMsg);
            }
            throw new Error(`PDF processing failed (${pdfResp.status}): ${errorMsg}`);
          }

          const pdfData = await pdfResp.json();
          preExtractedText = pdfData.text;
          preExtractedTotalPages = pdfData.total_pages;

          // Update totalPages if this is the first iteration and we got the actual page count from API
          if (totalPages === 1 && preExtractedTotalPages && preExtractedTotalPages > 1) {
            totalPages = preExtractedTotalPages;
            // Scale up batch size now that we know actual page count
            if (totalPages > 100) {
              pageBatchSize = Math.min(20, Math.max(pageBatchSize, Math.ceil(totalPages / 80)));
              console.log(`[DocumentsAdmin] Large PDF detected (${totalPages} pages), bumping batch size to ${pageBatchSize}`);
            }
            // Recalculate pageEnd with actual totalPages
            pageEnd = Math.min(nextPageStart + pageBatchSize - 1, totalPages);
            console.log(`[DocumentsAdmin] Updated total pages from API: ${totalPages}, adjusted pageEnd to ${pageEnd}`);
          }

          console.log(`[DocumentsAdmin] API extracted ${preExtractedText?.length ?? 0} chars (pages ${nextPageStart}-${pageEnd}/${totalPages})`);
        } catch (pdfError) {
          console.error('[DocumentsAdmin] PDF extraction failed:', pdfError);
          throw new Error(`PDF extraction failed: ${pdfError instanceof Error ? pdfError.message : String(pdfError)}`);
        }
      }

      // Calculate pageEnd for non-PDFs or for Supabase processing
      const pageEnd = Math.min(nextPageStart + pageBatchSize - 1, totalPages);

      // Call Supabase Edge Function for chunking + embeddings
      const procResp = await supabase.functions.invoke('ingest-doc', {
        body: {
          action: 'process',
          doc_id: docId,
          org_id: orgId,
          storage_path: storagePath,
          page_start: nextPageStart,
          page_end: pageEnd,
          // Pass pre-extracted text if available (from container API)
          ...(preExtractedText && {
            pre_extracted_text: preExtractedText,
            pre_extracted_total_pages: preExtractedTotalPages,
          }),
        },
      });

      if (procResp.error) {
        // Try parse error for WORKER_LIMIT; shrink batch and retry
        const message = await extractFunctionError(procResp);
        if (message.includes('WORKER_LIMIT') || /429/.test(message)) {
          pageBatchSize = Math.max(3, Math.floor(pageBatchSize / 2));
          console.warn('[DocumentsAdmin] Worker limit hit; reducing page batch size to', pageBatchSize);
          continue; // retry same start with smaller batch
        }
        throw new Error(message);
      }

      const data = procResp.data as any;
      nextPageStart = data?.next_page_start || (pageEnd + 1);
    }

    // Final status (optional)
    const statusResp = await supabase.functions.invoke('ingest-doc', {
      body: { action: 'status', doc_id: docId },
    });
    if (statusResp.error) {
      console.warn('[DocumentsAdmin] Status fetch failed:', await extractFunctionError(statusResp));
    } else {
      console.log('[DocumentsAdmin] Ingestion complete:', statusResp.data);
    }
  };

  const extractFunctionError = async (resp: any): Promise<string> => {
    try {
      const errorResponse = (resp.error as any)?.context || (resp as any)?.response;
      if (errorResponse) {
        // Try JSON first
        if (typeof errorResponse.json === 'function') {
          try {
            const errorData = await errorResponse.clone().json();
            return errorData.error || errorData.message || JSON.stringify(errorData);
          } catch {
            // Fallback to text (e.g., HTML or plain text error)
            if (typeof errorResponse.text === 'function') {
              const txt = await errorResponse.clone().text();
              return txt.slice(0, 300);
            }
          }
        }
      }
      return resp.error?.message || 'Unknown error';
    } catch (e) {
      return resp.error?.message || String(resp.error || e || 'Unknown error');
    }
  };

  // ===================================================================
  // Toggle active status
  // ===================================================================
  
  const handleToggleActive = async (doc: Document, e: React.MouseEvent) => {
    e.stopPropagation();
    
    try {
      const { error } = await supabase
        .from('doc_library')
        .update({ is_active: !doc.is_active })
        .eq('id', doc.id);

      if (error) {
        console.error('[DocumentsAdmin] Toggle active error:', error);
        alert('Failed to update document status.');
        return;
      }

      loadDocuments();
    } catch (error) {
      console.error('[DocumentsAdmin] Toggle active error:', error);
    }
  };

  // ===================================================================
  // Delete document
  // ===================================================================
  
  const handleDelete = async (doc: Document) => {
    if (!confirm(`Delete "${doc.title}"? This will also delete all associated chunks. This action cannot be undone.`)) {
      return;
    }

    try {
      // Delete from database (chunks cascade automatically)
      // Use .select() to get back deleted rows — if RLS blocks the delete, data will be empty
      const { data: deleted, error: dbError } = await supabase
        .from('doc_library')
        .delete()
        .eq('id', doc.id)
        .select('id');

      if (dbError) {
        console.error('[DocumentsAdmin] Delete error:', dbError);
        alert('Failed to delete document.');
        return;
      }

      if (!deleted || deleted.length === 0) {
        console.error('[DocumentsAdmin] Delete blocked by RLS — no rows affected for', doc.id);
        alert('Unable to delete this document. You may not have permission to delete it (e.g., it belongs to the Global airline).');
        return;
      }

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('documents')
        .remove([doc.storage_path]);

      if (storageError) {
        console.error('[DocumentsAdmin] Storage delete error:', storageError);
        // Non-critical: continue even if storage delete fails
      }

      alert(`Document "${doc.title}" deleted successfully.`);
      loadDocuments();
    } catch (error) {
      console.error('[DocumentsAdmin] Delete error:', error);
      alert('An unexpected error occurred during deletion.');
    }
  };

  // ===================================================================
  // Edit modal handlers
  // ===================================================================

  const handleOpenEditModal = (doc: Document) => {
    setSelectedDocument(doc);
    // Initialize edit form with current values
    setEditFormData({
      doc_type_id: doc.doc_type_id,
      fleet_model_id: doc.fleet_model_id,
      level: doc.level,
      effective_from: doc.effective_from,
      doc_boost: doc.doc_boost,
      k_override: doc.k_override,
      score_threshold_override: doc.score_threshold_override,
      alpha_hybrid_override: doc.alpha_hybrid_override,
      lambda_mmr_override: doc.lambda_mmr_override,
      field_weight_title_override: doc.field_weight_title_override,
      field_weight_body_override: doc.field_weight_body_override,
      field_weight_caption_override: doc.field_weight_caption_override,
      recency_decay_override: doc.recency_decay_override,
      assessment_boost_override: doc.assessment_boost_override,
    });
    setShowDetailsModal(true);
  };

  const handleSaveChanges = async () => {
    if (!selectedDocument) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('doc_library')
        .update({
          doc_type_id: editFormData.doc_type_id || null,
          fleet_model_id: editFormData.fleet_model_id || null,
          level: editFormData.level || null,
          effective_from: editFormData.effective_from || null,
          doc_boost: editFormData.doc_boost || null,
          k_override: editFormData.k_override || null,
          score_threshold_override: editFormData.score_threshold_override || null,
          alpha_hybrid_override: editFormData.alpha_hybrid_override || null,
          lambda_mmr_override: editFormData.lambda_mmr_override || null,
          field_weight_title_override: editFormData.field_weight_title_override || null,
          field_weight_body_override: editFormData.field_weight_body_override || null,
          field_weight_caption_override: editFormData.field_weight_caption_override || null,
          recency_decay_override: editFormData.recency_decay_override || null,
          assessment_boost_override: editFormData.assessment_boost_override || null,
        })
        .eq('id', selectedDocument.id);

      if (error) {
        console.error('[DocumentsAdmin] Save error:', error);
        alert('Failed to save changes.');
        setIsSaving(false);
        return;
      }

      alert('Changes saved successfully.');
      await loadDocuments();
      setShowDetailsModal(false);
    } catch (error) {
      console.error('[DocumentsAdmin] Save error:', error);
      alert('An unexpected error occurred while saving.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelChanges = () => {
    // Discard edit form data and close modal
    setEditFormData({});
    setShowDetailsModal(false);
  };

  // ===================================================================
  // Filter documents
  // ===================================================================

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  // ===================================================================
  // Render
  // ===================================================================
  
  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-navy">Document Library</h1>
          <p className="text-steel mt-1">
            Manage airline-specific SOPs, rubrics, and reference manuals for semantic search
          </p>
        </div>
        
        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-orca-elevate text-white rounded-lg hover:bg-orca-elevate/90 transition"
        >
          <Upload size={20} />
          Upload Document
        </button>
      </div>

      {/* Version Control Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
        <Info className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
        <div className="text-sm text-blue-900">
          <p className="font-semibold mb-1">📦 Version Control Enabled</p>
          <p className="text-blue-700">
            • Upload a file with an <strong>existing document title</strong> to create a new version and archive the old one<br />
            • View <strong>archived versions</strong> by setting Status filter to "Inactive" or "All Status"<br />
            • Click the eye icon on archived documents to <strong>restore</strong> or <strong>permanently delete</strong> them
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-steel" size={20} />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white text-navy placeholder-steel/50 border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate"
            />
          </div>

          {/* Doc Type Filter */}
          <select
            value={filters.doc_type}
            onChange={(e) => setFilters({ ...filters, doc_type: e.target.value })}
            className="px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate"
          >
            <option value="all">All Types</option>
            <option value="CoreSystem">Core System</option>
            <option value="RegulatorySystem">Regulatory System</option>
            <option value="InfoSystem">Information System</option>
            <option value="AirlineSpecific">Airline-Specific</option>
            <option value="Other">Other</option>
          </select>

          {/* Language Filter */}
          <select
            value={filters.language}
            onChange={(e) => setFilters({ ...filters, language: e.target.value })}
            className="px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate"
          >
            <option value="all">All Languages</option>
            <option value="en">English</option>
            <option value="fr">French</option>
            <option value="es">Spanish</option>
            <option value="de">German</option>
          </select>

          {/* Status Filter */}
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          {/* Airline Filter */}
          <select
            value={filters.airline}
            onChange={(e) => setFilters({ ...filters, airline: e.target.value })}
            className="px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate"
          >
            <option value="all">All Airlines</option>
            <option value="00000000-0000-0000-0000-000000000000">🌍 Global</option>
            {airlines.map(airline => (
              <option key={airline.id} value={airline.id}>
                {airline.name} ({airline.code})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Document List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="animate-spin text-orca-elevate" size={32} />
        </div>
      ) : filteredDocuments.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-md">
          <FileText className="mx-auto text-steel/50" size={48} />
          <p className="text-steel mt-4">No documents found. Upload your first document to get started!</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-x-auto">
          <table className="w-full">
            <thead className="bg-navy text-white">
              <tr>
                <th className="px-2 py-2 text-left text-sm">Title</th>
                <th className="px-2 py-2 text-left text-sm">Type</th>
                <th className="px-2 py-2 text-left text-sm">Airline</th>
                <th className="px-2 py-2 text-left text-sm">Language</th>
                <th className="px-2 py-2 text-left text-sm">Version</th>
                <th className="px-2 py-2 text-center text-sm">Chunks</th>
                <th className="px-2 py-2 text-left text-sm">Last Ingested</th>
                <th className="px-2 py-2 text-center text-sm">Status</th>
                <th className="px-2 py-2 text-center text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDocuments.map((doc) => (
                <tr key={doc.id} className="border-b border-steel/20 hover:bg-white transition">
                  <td className="px-2 py-2 font-semibold text-sm">
                    <div className="flex items-center gap-2">
                      {doc.title}
                      {!doc.is_active && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-xs font-normal">
                          <Archive size={12} />
                          Archived
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-sm">
                    <span className="px-2 py-0.5 bg-orca-elevate/20 text-orca-elevate rounded text-xs">
                      {doc.doc_type}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-sm">
                    <span className={doc.org_id === '00000000-0000-0000-0000-000000000000'
                      ? 'px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-semibold'
                      : 'px-2 py-0.5 bg-gray-100 text-gray-800 rounded text-xs'
                    }>
                      {airlines.find(a => a.id === doc.org_id)?.code || 'Unknown'}
                      {doc.org_id === '00000000-0000-0000-0000-000000000000' && ' 🌍'}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-steel text-sm">{doc.language.toUpperCase()}</td>
                  <td className="px-2 py-2 text-steel text-sm">
                    <span className="font-mono">{doc.version}</span>
                  </td>
                  <td className="px-2 py-2 text-center font-semibold text-sm">{doc.chunk_count}</td>
                  <td className="px-2 py-2 text-steel text-xs">
                    {doc.last_ingested_at
                      ? new Date(doc.last_ingested_at).toLocaleString()
                      : 'Never'
                    }
                  </td>
                  <td className="px-2 py-2 text-center">
                    <button
                      onClick={(e) => handleToggleActive(doc, e)}
                      className="p-1 hover:bg-steel/10 rounded"
                      title={doc.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {doc.is_active ? (
                        <CheckCircle className="inline text-green-500" size={18} />
                      ) : (
                        <XCircle className="inline text-red-500" size={18} />
                      )}
                    </button>
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => handleOpenEditModal(doc)}
                        className="p-1 hover:bg-steel/10 rounded relative group"
                        title={doc.is_active ? "View Details" : "View Details • Restore or Delete Archived Version"}
                      >
                        <Eye size={16} className="text-steel" />
                        {!doc.is_active && (
                          <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 rounded-full"></span>
                        )}
                      </button>

                      <button
                        onClick={() => handleReingest(doc)}
                        className="p-1 hover:bg-orca-elevate/10 rounded"
                        title={reingestingDocIds.has(doc.id) ? "Re-ingesting..." : "Re-ingest"}
                        disabled={reingestingDocIds.has(doc.id)}
                      >
                        <RefreshCw size={16} className={reingestingDocIds.has(doc.id) ? "text-orca-elevate animate-spin" : "text-orca-elevate"} />
                      </button>

                      <button
                        onClick={() => handleDelete(doc)}
                        className="p-1 hover:bg-red-100 rounded"
                        title="Delete"
                      >
                        <Trash2 size={16} className="text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Replace & Archive Modal */}
      {showReplaceModal && replaceContext && uploadFile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-2xl font-bold text-navy mb-3">Replace existing document?</h2>
            <p className="text-sm text-navy mb-3">
              A document titled “{replaceContext.existingDoc.title}” already exists. Uploading this file will
              create version {replaceContext.nextVersion} and move the current file to Archive. The previous version remains restorable or permanently deletable.
            </p>
            <div className="bg-white rounded border border-steel/20 p-3 text-sm mb-3">
              <div className="mb-1">Current: {replaceContext.existingDoc.version} • {replaceContext.existingDoc.storage_path.split('/').pop()}</div>
              <div className="mb-1">New file: {uploadFile.name} • {(uploadFile.size / 1024 / 1024).toFixed(2)} MB</div>
              <div className="mb-1">Version to create: {replaceContext.nextVersion} (auto)</div>
              <div className="text-xs text-steel">
                Archive path: /org/{replaceContext.existingDoc.org_id}/docs/archive/{replaceContext.existingDoc.id}/{replaceContext.existingDoc.version}/{replaceContext.existingDoc.storage_path.split('/').pop()}
              </div>
            </div>
            <div className="space-y-2 text-sm mb-4">
              <label className="block font-semibold">Title (prefilled)</label>
              <input
                type="text"
                value={uploadForm.title}
                onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                className="w-full px-3 py-1.5 border border-steel/30 rounded-lg text-sm"
              />
              <label className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  checked={uploadForm.is_active}
                  onChange={(e) => setUploadForm({ ...uploadForm, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <span>Active (included in search)</span>
              </label>
              <p className="text-xs text-steel">You can change the document Type after upload from the details panel.</p>
            </div>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => { setShowReplaceModal(false); setReplaceContext(null); }}
                className="px-4 py-2 border border-steel/30 rounded-lg hover:bg-steel/10 transition text-sm"
              >
                Cancel
              </button>
              <button
                onClick={performUploadAsNew}
                className="px-4 py-2 border border-orca-elevate text-orca-elevate rounded-lg hover:bg-orca-elevate/10 transition text-sm flex items-center gap-1"
              >
                <Upload size={16} />
                Upload as New
              </button>
              <button
                onClick={performReplaceAndArchive}
                className="px-4 py-2 bg-orca-elevate text-white rounded-lg hover:bg-orca-elevate/90 transition text-sm flex items-center gap-1"
              >
                <Archive size={16} />
                Replace & Archive
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-2xl font-bold text-navy mb-3">Upload Document</h2>

            <div className="space-y-2.5">
              {/* File Input */}
              <div>
                <label className="block text-sm font-semibold text-navy mb-1">File</label>
                <input
                  type="file"
                  accept=".pdf,.docx,.txt"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setUploadFile(f);
                    if (f && !uploadForm.title) {
                      setUploadForm({ ...uploadForm, title: getFilenameSansExt(f.name) });
                    }
                  }}
                  className="w-full px-3 py-1.5 border border-steel/30 rounded-lg text-sm"
                />
                <p className="text-xs text-steel mt-0.5">Supported formats: PDF, DOCX, TXT (max 50MB)</p>
              </div>

              {/* Airline Assignment */}
              <div>
                <label className="block text-sm font-semibold text-navy mb-1">
                  Assign to Airline
                  <span className="ml-1 text-xs font-normal text-steel">(required)</span>
                </label>
                <select
                  value={uploadForm.airline_id}
                  onChange={(e) => setUploadForm({ ...uploadForm, airline_id: e.target.value })}
                  className="w-full px-3 py-1.5 border border-steel/30 rounded-lg text-sm"
                  required
                >
                  <option value="">Select airline...</option>
                  {userAirlines
                    .filter(a => a.role === 'admin' || a.role === 'editor')
                    .map(airline => (
                      <option key={airline.airline_id} value={airline.airline_id}>
                        {airline.name} ({airline.code})
                        {airline.airline_id === '00000000-0000-0000-0000-000000000000' && ' 🌍'}
                      </option>
                    ))}
                </select>
                <p className="text-xs text-steel mt-0.5">
                  {uploadForm.airline_id === '00000000-0000-0000-0000-000000000000'
                    ? '🌍 This document will be accessible to all airlines'
                    : 'This document will only be accessible to the selected airline'
                  }
                </p>
              </div>

              {/* Version Control Hint */}
              <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-800">
                <p className="font-semibold mb-1">💡 Version Control Tip</p>
                <p>If the <strong>Title</strong> matches an existing document, you'll be prompted to create a new version and archive the old one.</p>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-navy mb-1">
                  Title
                  <span className="ml-1 text-xs font-normal text-steel">(auto-filled from filename)</span>
                </label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                  placeholder="e.g., A320 SOPs v3.2"
                  className="w-full px-3 py-1.5 border border-steel/30 rounded-lg text-sm"
                />
              </div>

              {/* Doc Type */}
              <div>
                <label className="block text-sm font-semibold text-navy mb-1">Type</label>
                <select
                  value={uploadForm.doc_type}
                  onChange={(e) => setUploadForm({ ...uploadForm, doc_type: e.target.value as Document['doc_type'] })}
                  className="w-full px-3 py-1.5 border border-steel/30 rounded-lg text-sm"
                >
                  <option value="CoreSystem">Core System</option>
                  <option value="RegulatorySystem">Regulatory System</option>
                  <option value="InfoSystem">Information System</option>
                  <option value="AirlineSpecific">Airline-Specific</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Language */}
              <div>
                <label className="block text-sm font-semibold text-navy mb-1">Language</label>
                <select
                  value={uploadForm.language}
                  onChange={(e) => setUploadForm({ ...uploadForm, language: e.target.value })}
                  className="w-full px-3 py-1.5 border border-steel/30 rounded-lg text-sm"
                >
                  <option value="en">English</option>
                  <option value="fr">French</option>
                  <option value="es">Spanish</option>
                  <option value="de">German</option>
                </select>
              </div>

              {/* Version */}
              <div>
                <label className="block text-sm font-semibold text-navy mb-1">Version</label>
                <input
                  type="text"
                  value={uploadForm.version}
                  onChange={(e) => setUploadForm({ ...uploadForm, version: e.target.value })}
                  placeholder="e.g., v3.2"
                  className="w-full px-3 py-1.5 border border-steel/30 rounded-lg text-sm"
                />
              </div>

              {/* Active Checkbox */}
              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  checked={uploadForm.is_active}
                  onChange={(e) => setUploadForm({ ...uploadForm, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <label className="text-sm text-navy">Active (included in search)</label>
              </div>

              {/* Progress Bar */}
              {uploading && (
                <div className="w-full bg-steel/20 rounded-full h-2 mt-2">
                  <div
                    className="bg-orca-elevate h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-center gap-3 mt-4">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  resetUploadForm();
                }}
                disabled={uploading}
                className="px-4 py-2 border border-steel/30 rounded-lg hover:bg-steel/10 transition disabled:opacity-50 text-sm"
              >
                Cancel
              </button>

              <button
                onClick={handleUploadOnly}
                disabled={uploading || !uploadFile || !uploadForm.airline_id}
                className="px-4 py-2 border border-orca-elevate text-orca-elevate rounded-lg hover:bg-orca-elevate/10 transition disabled:opacity-50 text-sm"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>

              <button
                onClick={handleUploadAndIngest}
                disabled={uploading || !uploadFile || !uploadForm.airline_id}
                className="px-4 py-2 bg-orca-elevate text-white rounded-lg hover:bg-orca-elevate/90 transition disabled:opacity-50 text-sm"
              >
                {uploading ? 'Processing...' : 'Upload & Ingest'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedDocument && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Fixed Header */}
            <div className="flex items-center gap-3 px-6 py-4 border-b border-steel/20">
              <h2 className="text-xl font-bold text-navy">{selectedDocument.title}</h2>
              {!selectedDocument.is_active && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold">
                  <Archive size={14} />
                  Archived
                </span>
              )}
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="space-y-2">
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div>
                  <p className="text-steel">Type</p>
                  <p className="font-semibold text-navy">{selectedDocument.doc_type}</p>
                </div>
                <div>
                  <p className="text-steel">Language</p>
                  <p className="font-semibold text-navy">{selectedDocument.language.toUpperCase()}</p>
                </div>
                <div>
                  <p className="text-steel">Version</p>
                  <p className="font-semibold text-navy">{selectedDocument.version}</p>
                </div>
                <div>
                  <p className="text-steel">Chunks</p>
                  <p className="font-semibold text-navy">{selectedDocument.chunk_count}</p>
                </div>
                <div>
                  <p className="text-steel">File Size</p>
                  <p className="font-semibold text-navy">
                    {selectedDocument.file_size_bytes
                      ? `${(selectedDocument.file_size_bytes / 1024 / 1024).toFixed(2)} MB`
                      : 'Unknown'
                    }
                  </p>
                </div>
                <div>
                  <p className="text-steel">Pages</p>
                  <p className="font-semibold text-navy">{selectedDocument.page_count || 'Unknown'}</p>
                </div>
              </div>

              <div className="text-xs">
                <p className="text-steel">Last Ingested</p>
                <p className="font-semibold text-navy">
                  {selectedDocument.last_ingested_at
                    ? new Date(selectedDocument.last_ingested_at).toLocaleString()
                    : 'Never'
                  }
                </p>
              </div>

              <div className="text-xs">
                <p className="text-steel">Storage Path</p>
                <p className="font-mono bg-white p-1.5 rounded border border-steel/20 text-[10px]">{selectedDocument.storage_path}</p>
              </div>

              {/* Airline Assignment */}
              <div className="text-xs">
                <p className="text-steel mb-1">
                  Assign to Airline
                  <span className="ml-1 text-xs font-normal text-steel">(required)</span>
                </p>
                <select
                  value={selectedDocument.org_id}
                  onChange={async (e) => {
                    const newAirlineId = e.target.value;
                    if (!newAirlineId) {
                      alert('Please select an airline.');
                      return;
                    }

                    // Update doc_library.org_id
                    const { error } = await supabase
                      .from('doc_library')
                      .update({ org_id: newAirlineId })
                      .eq('id', selectedDocument.id);

                    if (error) {
                      console.error('[DocumentEdit] Failed to update airline:', error);
                      alert('Failed to update document airline assignment.');
                      return;
                    }

                    // Also update doc_chunks.org_id so vector search finds them under the new airline
                    const { error: chunksError } = await supabase
                      .from('doc_chunks')
                      .update({ org_id: newAirlineId })
                      .eq('doc_id', selectedDocument.id);

                    if (chunksError) {
                      console.error('[DocumentEdit] Failed to update chunk airline:', chunksError);
                      alert('Document updated but chunk airline sync failed. Re-ingest to fix.');
                    }

                    // Update local state
                    setSelectedDocument({ ...selectedDocument, org_id: newAirlineId });
                    loadDocuments();
                  }}
                  className="w-full px-2 py-1 border border-steel/30 rounded text-xs"
                >
                  <option value="">Select airline...</option>
                  {airlines.map(airline => (
                    <option key={airline.id} value={airline.id}>
                      {airline.name} ({airline.code})
                      {airline.id === '00000000-0000-0000-0000-000000000000' && ' 🌍'}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-steel mt-0.5">
                  {selectedDocument.org_id === '00000000-0000-0000-0000-000000000000'
                    ? '🌍 This document is accessible to all airlines'
                    : 'This document is accessible only to the selected airline'
                  }
                </p>
              </div>

              {/* Inline Type editing */}
              <div className="text-xs">
                <p className="text-steel mb-1">Edit Type</p>
                <select
                  value={selectedDocument.doc_type}
                  onChange={async (e) => {
                    const newType = e.target.value as Document['doc_type'];
                    const { error } = await supabase
                      .from('doc_library')
                      .update({ doc_type: newType })
                      .eq('id', selectedDocument.id);
                    if (error) {
                      alert('Failed to update document type.');
                      return;
                    }
                    setSelectedDocument({ ...selectedDocument, doc_type: newType });
                    loadDocuments();
                  }}
                  className="w-full px-2 py-1 border border-steel/30 rounded text-xs"
                >
                  <option value="CoreSystem">Core System</option>
                  <option value="RegulatorySystem">Regulatory System</option>
                  <option value="InfoSystem">Information System</option>
                  <option value="AirlineSpecific">Airline-Specific</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Airline Hierarchy Section */}
              <div className="border-t border-steel/20 pt-2 mt-2">
                <h3 className="text-sm font-semibold text-navy mb-2 flex items-center gap-1">
                  <Settings size={14} />
                  Airline Hierarchy
                </h3>

                <div className="grid grid-cols-2 gap-2 text-xs">
                  {/* Doc Type ID */}
                  <div>
                    <label className="text-steel block mb-0.5">Document Type ID</label>
                    <select
                      value={editFormData.doc_type_id || ''}
                      onChange={(e) => {
                        const newDocTypeId = e.target.value || undefined;
                        setEditFormData({ ...editFormData, doc_type_id: newDocTypeId });
                      }}
                      className="w-full px-2 py-1 border border-steel/30 rounded text-xs"
                    >
                      <option value="">None</option>
                      {docTypes.map(dt => (
                        <option key={dt.id} value={dt.id}>
                          {dt.label} ({dt.code})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Fleet Model */}
                  <div>
                    <label className="text-steel block mb-0.5">Fleet Model</label>
                    <select
                      value={editFormData.fleet_model_id || ''}
                      onChange={(e) => {
                        const newFleetId = e.target.value || undefined;
                        setEditFormData({ ...editFormData, fleet_model_id: newFleetId });
                      }}
                      className="w-full px-2 py-1 border border-steel/30 rounded text-xs"
                    >
                      <option value="">None (All Aircraft)</option>
                      {fleetModels.map(fm => (
                        <option key={fm.id} value={fm.id}>
                          {fm.name} ({fm.code})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Training Level */}
                  <div>
                    <label className="text-steel block mb-0.5">Training Level</label>
                    <select
                      value={editFormData.level || ''}
                      onChange={(e) => {
                        const newLevel = (e.target.value as TrainingLevel) || undefined;
                        setEditFormData({ ...editFormData, level: newLevel });
                      }}
                      className="w-full px-2 py-1 border border-steel/30 rounded text-xs"
                    >
                      <option value="">None (All Levels)</option>
                      <option value="Level1">Level 1</option>
                      <option value="Level2">Level 2</option>
                      <option value="Level3">Level 3</option>
                    </select>
                  </div>

                  {/* Effective From */}
                  <div>
                    <label className="text-steel block mb-0.5">Effective From</label>
                    <input
                      type="date"
                      value={editFormData.effective_from ? editFormData.effective_from.split('T')[0] : ''}
                      onChange={(e) => {
                        const newDate = e.target.value || undefined;
                        setEditFormData({ ...editFormData, effective_from: newDate });
                      }}
                      className="w-full px-2 py-1 border border-steel/30 rounded text-xs"
                    />
                  </div>
                </div>
              </div>

              {/* Advanced Retrieval Parameters Section */}
              <div className="border-t border-steel/20 pt-2 mt-2">
                <button
                  onClick={() => setShowAdvancedParams(!showAdvancedParams)}
                  className="flex items-center justify-between w-full text-left mb-1 hover:bg-steel/5 p-1 rounded transition"
                >
                  <h3 className="text-sm font-semibold text-navy flex items-center gap-1">
                    <Settings size={14} />
                    Advanced Retrieval Parameters
                  </h3>
                  {showAdvancedParams ? (
                    <ChevronUp size={16} className="text-steel" />
                  ) : (
                    <ChevronDown size={16} className="text-steel" />
                  )}
                </button>

                {showAdvancedParams && (
                  <div className="space-y-2 bg-white p-2 rounded border border-steel/20">
                    <p className="text-[10px] text-steel mb-1">
                      Override defaults. Leave empty to use doc type defaults.
                    </p>

                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {/* Document Boost */}
                      <div>
                        <label className="text-steel block mb-0.5">Doc Boost</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="10"
                          value={editFormData.doc_boost ?? ''}
                          onChange={(e) => {
                            const val = e.target.value ? parseFloat(e.target.value) : undefined;
                            setEditFormData({ ...editFormData, doc_boost: val });
                          }}
                          placeholder="1.0"
                          className="w-full px-2 py-1 border border-steel/30 rounded text-xs"
                        />
                      </div>

                      {/* K Override */}
                      <div>
                        <label className="text-steel block mb-0.5">Top-K</label>
                        <input
                          type="number"
                          step="1"
                          min="1"
                          max="50"
                          value={editFormData.k_override ?? ''}
                          onChange={(e) => {
                            const val = e.target.value ? parseInt(e.target.value) : undefined;
                            setEditFormData({ ...editFormData, k_override: val });
                          }}
                          placeholder="5"
                          className="w-full px-2 py-1 border border-steel/30 rounded text-xs"
                        />
                      </div>

                      {/* Score Threshold Override */}
                      <div>
                        <label className="text-steel block mb-0.5">Score Threshold</label>
                        <input
                          type="number"
                          step="0.05"
                          min="0"
                          max="1"
                          value={editFormData.score_threshold_override ?? ''}
                          onChange={(e) => {
                            const val = e.target.value ? parseFloat(e.target.value) : undefined;
                            setEditFormData({ ...editFormData, score_threshold_override: val });
                          }}
                          placeholder="0.3"
                          className="w-full px-2 py-1 border border-steel/30 rounded text-xs"
                        />
                      </div>

                      {/* Alpha Hybrid Override */}
                      <div>
                        <label className="text-steel block mb-0.5">Alpha Hybrid</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="1"
                          value={editFormData.alpha_hybrid_override ?? ''}
                          onChange={(e) => {
                            const val = e.target.value ? parseFloat(e.target.value) : undefined;
                            setEditFormData({ ...editFormData, alpha_hybrid_override: val });
                          }}
                          placeholder="0.7"
                          className="w-full px-2 py-1 border border-steel/30 rounded text-xs"
                        />
                      </div>

                      {/* Lambda MMR Override */}
                      <div>
                        <label className="text-steel block mb-0.5">Lambda MMR</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="1"
                          value={editFormData.lambda_mmr_override ?? ''}
                          onChange={(e) => {
                            const val = e.target.value ? parseFloat(e.target.value) : undefined;
                            setEditFormData({ ...editFormData, lambda_mmr_override: val });
                          }}
                          placeholder="0.5"
                          className="w-full px-2 py-1 border border-steel/30 rounded text-xs"
                        />
                      </div>

                      {/* Field Weight Title Override */}
                      <div>
                        <label className="text-steel block mb-0.5">Weight: Title</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="5"
                          value={editFormData.field_weight_title_override ?? ''}
                          onChange={(e) => {
                            const val = e.target.value ? parseFloat(e.target.value) : undefined;
                            setEditFormData({ ...editFormData, field_weight_title_override: val });
                          }}
                          placeholder="2.0"
                          className="w-full px-2 py-1 border border-steel/30 rounded text-xs"
                        />
                      </div>

                      {/* Field Weight Body Override */}
                      <div>
                        <label className="text-steel block mb-0.5">Weight: Body</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="5"
                          value={editFormData.field_weight_body_override ?? ''}
                          onChange={(e) => {
                            const val = e.target.value ? parseFloat(e.target.value) : undefined;
                            setEditFormData({ ...editFormData, field_weight_body_override: val });
                          }}
                          placeholder="1.0"
                          className="w-full px-2 py-1 border border-steel/30 rounded text-xs"
                        />
                      </div>

                      {/* Field Weight Caption Override */}
                      <div>
                        <label className="text-steel block mb-0.5">Weight: Caption</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="5"
                          value={editFormData.field_weight_caption_override ?? ''}
                          onChange={(e) => {
                            const val = e.target.value ? parseFloat(e.target.value) : undefined;
                            setEditFormData({ ...editFormData, field_weight_caption_override: val });
                          }}
                          placeholder="1.5"
                          className="w-full px-2 py-1 border border-steel/30 rounded text-xs"
                        />
                      </div>

                      {/* Recency Decay Override */}
                      <div>
                        <label className="text-steel block mb-0.5">Recency Decay</label>
                        <input
                          type="number"
                          step="0.001"
                          min="0"
                          max="1"
                          value={editFormData.recency_decay_override ?? ''}
                          onChange={(e) => {
                            const val = e.target.value ? parseFloat(e.target.value) : undefined;
                            setEditFormData({ ...editFormData, recency_decay_override: val });
                          }}
                          placeholder="0.001"
                          className="w-full px-2 py-1 border border-steel/30 rounded text-xs"
                        />
                      </div>

                      {/* Assessment Boost Override */}
                      <div>
                        <label className="text-steel block mb-0.5">Assessment Boost</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="5"
                          value={editFormData.assessment_boost_override ?? ''}
                          onChange={(e) => {
                            const val = e.target.value ? parseFloat(e.target.value) : undefined;
                            setEditFormData({ ...editFormData, assessment_boost_override: val });
                          }}
                          placeholder="1.5"
                          className="w-full px-2 py-1 border border-steel/30 rounded text-xs"
                        />
                      </div>
                    </div>

                    {/* Reset All Overrides Button */}
                    <div className="border-t border-steel/20 pt-2 mt-2">
                      <button
                        onClick={() => {
                          if (!confirm('Reset all retrieval parameter overrides to doc type defaults?')) return;
                          setEditFormData({
                            ...editFormData,
                            doc_boost: undefined,
                            k_override: undefined,
                            score_threshold_override: undefined,
                            alpha_hybrid_override: undefined,
                            lambda_mmr_override: undefined,
                            field_weight_title_override: undefined,
                            field_weight_body_override: undefined,
                            field_weight_caption_override: undefined,
                            recency_decay_override: undefined,
                            assessment_boost_override: undefined,
                          });
                        }}
                        className="px-3 py-1.5 bg-amber-500 text-white rounded hover:bg-amber-600 transition text-xs flex items-center gap-1"
                      >
                        <RotateCcw size={12} />
                        Reset All Overrides
                      </button>
                    </div>
                  </div>
                )}
              </div>

            {/* Archived Version Actions Banner */}
            {!selectedDocument.is_active && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2 mt-2">
                <p className="text-xs font-semibold text-amber-900 mb-1">📦 Archived Version</p>
                <p className="text-[10px] text-amber-800">
                  Restore as new version or permanently delete.
                </p>
              </div>
            )}
            </div>
            </div>

            {/* Fixed Footer with Action Buttons */}
            <div className="border-t border-steel/20 px-6 py-3 bg-white flex justify-between gap-2">
              <div className="flex gap-2">
                {!selectedDocument.is_active && (
                  <>
                    <button
                      onClick={async () => {
                        try {
                          const sameTitleDocs = documents.filter(d => d.title === selectedDocument.title);
                          const maxVersion = sameTitleDocs.reduce((acc, d) => Math.max(acc, (d.version ? (parseInt((d.version.match(/^v?(\d+)/i)?.[1] || '0'), 10) || 0) : 0)), 0);
                          const nextVer = `v${maxVersion + 1}`;
                          const { data: newDoc, error: createErr } = await supabase
                            .from('doc_library')
                            .insert({
                              org_id: selectedDocument.org_id,
                              title: selectedDocument.title,
                              doc_type: selectedDocument.doc_type,
                              language: selectedDocument.language,
                              version: nextVer,
                              is_active: true,
                              storage_path: '',
                              file_size_bytes: selectedDocument.file_size_bytes,
                              created_by: selectedDocument.created_by,
                            })
                            .select()
                            .single();
                          if (createErr || !newDoc) {
                            alert('Failed to create new version for restore.');
                            return;
                          }
                          const filename = selectedDocument.storage_path.split('/').pop() || 'file';
                          const newPath = `/org/${selectedDocument.org_id}/docs/${newDoc.id}/${filename}`;
                          const { error: copyErr } = await supabase.storage
                            .from('documents')
                            .copy(selectedDocument.storage_path, newPath);
                          if (copyErr) {
                            alert('Failed to copy archived file.');
                            await supabase.from('doc_library').delete().eq('id', newDoc.id);
                            return;
                          }
                          await supabase.from('doc_library').update({ storage_path: newPath }).eq('id', newDoc.id);
                          alert(`Restored as ${nextVer}.`);
                          loadDocuments();
                          setShowDetailsModal(false);
                        } catch (e) {
                          alert('Unexpected error during restore.');
                        }
                      }}
                      className="px-3 py-1.5 bg-orca-elevate text-white rounded hover:bg-orca-elevate/90 transition flex items-center gap-1 text-xs"
                    >
                      <RotateCcw size={14} />
                      Restore
                    </button>
                    <button
                      onClick={async () => {
                        if (!confirm('Permanently delete this archived version?')) return;
                        try {
                          await supabase
                            .from('doc_library')
                            .delete()
                            .eq('id', selectedDocument.id);
                          await supabase.storage.from('documents').remove([selectedDocument.storage_path]);
                          loadDocuments();
                          setShowDetailsModal(false);
                        } catch {
                          alert('Failed to delete archived version.');
                        }
                      }}
                      className="px-3 py-1.5 bg-red-500 text-white rounded hover:bg-red-600 transition flex items-center gap-1 text-xs"
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCancelChanges}
                  className="px-3 py-1.5 bg-steel/20 text-steel rounded hover:bg-steel/30 transition text-xs"
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveChanges}
                  className="px-3 py-1.5 bg-orca-elevate text-white rounded hover:bg-orca-elevate/90 transition text-xs flex items-center gap-1"
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <Loader size={14} className="animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={14} />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsAdmin;



